import _defineProperty from "/opt/build/repo/website/node_modules/@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { Map, Record } from 'immutable';
import { useMemo } from 'react';
import { bindToDispatch } from './dispatch';
import { Module } from './module';
import { useComposeAppGlobalState } from './composeAppContext';
import { effect } from './core/effects/composeEffects';
export function composeActionCreators(actionMap) {
  return Object.keys(actionMap).reduce(function (actionCreators, key) {
    return Object.assign(actionCreators, _defineProperty({}, key, bindToDispatch(actionMap[key].create)));
  }, {});
}
export function composeInitialState(initialState) {
  return Record(initialState)();
}
export function composeReducer(actionMap, reducerCallback) {
  function actionReducer(actionComposer, reduce) {
    return {
      reducerType: 'payload',
      actionType: actionComposer.actionType,
      reduce: reduce
    };
  }

  function payloadlessActionReducer(actionComposer, reduce) {
    return {
      reducerType: 'payloadless',
      actionType: actionComposer.actionType,
      reduce: reduce
    };
  }

  var actions = Map(actionMap).valueSeq().concat(reducerCallback ? reducerCallback({
    actionReducer: actionReducer,
    payloadlessActionReducer: payloadlessActionReducer
  }) : []).filter(function (ac) {
    return ac.reducerType !== 'none';
  });
  return function (state, action) {
    var reducer = actions.find(function (ac) {
      return ac.actionType === action.type;
    });

    if (!reducer) {
      return state;
    }

    if (reducer.reducerType === 'payload') {
      return reducer.reduce(state, action.payload);
    }

    return reducer.reduce(state);
  };
}
export var createNamespacer = function createNamespacer() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return function (actionType) {
    return [].concat(args, [actionType]).join('/');
  };
};
export var composeModule = function composeModule(moduleName, moduleVersion) {
  var moduleNamespace = createNamespacer(moduleName, moduleVersion);

  var composeStateMachine = function composeStateMachine(reducerName, initialState, callback, reducerCallback) {
    var reducerNamespace = createNamespacer(reducerName);
    var reducerBranchKey = moduleNamespace(reducerName);

    var namespacer = function namespacer(k) {
      return moduleNamespace(reducerNamespace(k));
    };

    function action(reduce) {
      return {
        reducerType: 'payload',
        actionType: '',
        reduce: reduce,
        create: function create(payload) {
          return {
            type: '',
            payload: payload
          };
        }
      };
    }

    function payloadlessAction(reduce) {
      return {
        reducerType: 'payloadless',
        actionType: '',
        reduce: reduce,
        create: function create() {
          return {
            type: ''
          };
        }
      };
    }

    var actionMap = callback({
      action: action,
      payloadlessAction: payloadlessAction
    });
    var actions = Object.keys(actionMap).reduce(function (result, key) {
      return Object.assign(result, _defineProperty({}, key, Object.assign({}, actionMap[key], {
        actionType: namespacer(key),
        create: actionMap[key].reducerType === 'payload' ? function (payload) {
          return {
            type: namespacer(key),
            payload: payload
          };
        } : function () {
          return {
            type: namespacer(key)
          };
        }
      })));
    }, {});
    return {
      actions: actions,
      actionCreators: composeActionCreators(actions),
      branchKey: reducerBranchKey,
      initialState: initialState,
      reducer: composeReducer(actions, reducerCallback),
      selector: function selector(state) {
        return state.get(reducerBranchKey);
      },
      useStateMachine: function useStateMachine(cb) {
        var branchState = useComposeAppGlobalState(function (state) {
          return state.get(reducerBranchKey);
        });
        return useMemo(function () {
          return cb(branchState);
        }, [branchState]);
      }
    };
  };

  var composeEffects = function composeEffects(namespace, callback) {
    var namespacer = createNamespacer(moduleName, namespace);

    function tempEffect(run) {
      return {
        kind: 'effect',
        run: run
      };
    }

    function tempPayloadlessEffect(run) {
      return {
        kind: 'payloadlessEffect',
        run: run
      };
    }

    function tempStatefulEffect(selector, run) {
      return {
        kind: 'statefulEffect',
        selector: selector,
        run: run
      };
    }

    function tempStatefulPayloadlessEffect(selector, run) {
      return {
        kind: 'statefulPayloadlessEffect',
        selector: selector,
        run: run
      };
    }

    var actionMap = callback({
      effect: tempEffect,
      payloadlessEffect: tempPayloadlessEffect,
      statefulEffect: tempStatefulEffect,
      statefulPayloadlessEffect: tempStatefulPayloadlessEffect
    });
    return Object.keys(actionMap).reduce(function (result, key) {
      return Object.assign(result, _defineProperty({}, key, effect(namespacer(key), actionMap[key].run, actionMap[key].kind, actionMap[key].selector)));
    }, {});
  };

  var composeMiddleware = function composeMiddleware(middleware) {
    return middleware;
  };

  var composeSubscriber = function composeSubscriber(subscriber) {
    return subscriber;
  };

  return {
    composeStateMachine: composeStateMachine,
    composeEffects: composeEffects,
    composeMiddleware: composeMiddleware,
    composeSubscriber: composeSubscriber,
    namespace: createNamespacer(moduleName),
    exportModule: function exportModule(mod) {
      return Module(_objectSpread(_objectSpread({}, mod), {}, {
        name: "".concat(moduleName, "/").concat(moduleVersion)
      }));
    }
  };
};